import React, { useEffect, useRef, useState, useId } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { Button, PowerIcon } from './Primitives';
import { ProductBadges, PowerStateBadge } from './VmMeta';
import { actionMeta, ACTION } from '../utils/constants';

/**
 * Modal di conferma per accensione/spegnimento. Accessibile da tastiera:
 * role="dialog" + aria-modal, focus iniziale sul primo controllo, chiusura con
 * Escape, focus loop entro il dialog, ripristino del focus alla chiusura.
 * Mostra nome VM, ambiente, prodotti, stato attuale, azione richiesta, un campo
 * facoltativo per la motivazione e — per lo shutdown — l'avviso su VMware Tools.
 */
export default function VmActionModal({ vm, action, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const previouslyFocused = useRef(null);
  const titleId = useId();
  const descId = useId();

  const meta = actionMeta(action);
  const isShutdown = action === ACTION.SHUTDOWN;

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    // Focus iniziale.
    const t = setTimeout(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, 0);

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        // Focus trap semplice entro il dialog.
        const focusables = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const list = Array.from(focusables).filter((el) => !el.disabled);
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKey, true);
      // Ripristina il focus al trigger.
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        previouslyFocused.current.focus();
      }
    };
  }, [onClose]);

  if (!vm || !meta) return null;

  const handleConfirm = () => onConfirm(vm, action, reason);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="presentation"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-5 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white ${
                isShutdown ? 'bg-amber-600' : 'bg-sky-600'
              }`}
            >
              <PowerIcon name={meta.icon} size={18} aria-hidden="true" />
            </span>
            <div>
              <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
                Conferma: {meta.label}
              </h2>
              <p id={descId} className="text-xs text-gray-500 dark:text-gray-400">
                Stai per richiedere l’azione «{meta.verb}» sulla VM selezionata.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 dark:hover:text-gray-200"
            aria-label="Chiudi finestra di conferma"
          >
            <FaTimes size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {/* Riepilogo VM */}
          <dl className="grid grid-cols-3 gap-3 rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <div className="col-span-3 sm:col-span-1">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400">Nome VM</dt>
              <dd className="font-medium text-gray-800 dark:text-gray-100">{vm.name}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wide text-gray-400">Ambiente</dt>
              <dd className="text-gray-700 dark:text-gray-200">{vm.environment || '—'}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wide text-gray-400">Stato attuale</dt>
              <dd><PowerStateBadge state={vm.powerState} /></dd>
            </div>
            <div className="col-span-3">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400">Prodotti</dt>
              <dd className="mt-1"><ProductBadges vm={vm} /></dd>
            </div>
            <div className="col-span-3">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400">Azione richiesta</dt>
              <dd className="font-medium text-gray-800 dark:text-gray-100">
                {meta.verb} → stato atteso: {meta.targetLabel}
              </dd>
            </div>
          </dl>

          {/* Avviso VMware Tools per lo shutdown */}
          {isShutdown && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              <FaExclamationTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <p>
                Lo spegnimento è un <strong>arresto guest controllato</strong> e
                dipende da VMware Tools attivo nella VM. Se gli strumenti non sono
                in esecuzione l’operazione può fallire; non verrà mai forzato lo
                spegnimento dell’alimentazione.
              </p>
            </div>
          )}

          {/* Motivazione facoltativa */}
          <div>
            <label
              htmlFor="esxi-action-reason"
              className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
            >
              Motivazione (facoltativa)
            </label>
            <textarea
              id="esxi-action-reason"
              ref={firstFieldRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Es. Avvio ambiente di test per collaudo…"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant={isShutdown ? 'warning' : 'info'}
            icon={undefined}
            onClick={handleConfirm}
          >
            <PowerIcon name={meta.icon} size={14} aria-hidden="true" />
            {meta.label}
          </Button>
        </div>
      </div>
    </div>
  );
}
